import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";

import { boundedNote } from "../board.ts";
import type { Session } from "../session.ts";
import {
  freshnessOf,
  INVENTORY_SCOPE,
  listingNote,
  statesPresent,
  stockRow,
  UPDATE_REFUSAL,
  type Availability,
} from "../stock.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `stock` in `22 WP21 Merchant Portal.dc.html`: report stock so customers see a live count.
 *
 * **The one design file on this platform that names its own absence and prescribes the fix.** Its
 * closing note is `G-36: the service stores only a count and time, so freshness is worked out from
 * age` — so the design knows COM-03 publishes no freshness flag and tells the build to derive one.
 * Every other absence in this repository is recorded against a design; this one is recorded by it,
 * and `freshnessOf` in `../stock.ts` is the instruction carried out with the threshold read off the
 * design's own `Stale · 31 · over 30 min` tile rather than chosen.
 *
 * What is drawn from data and what is not — `../stock.ts` argues each:
 *
 * | drawn                            | source                                                    |
 * | -------------------------------- | --------------------------------------------------------- |
 * | the rows                         | COM-03's location listing                                 |
 * | `Product` — `Avocado, Hass`      | `sku`. The name is COM-02's `products.name`               |
 * | `Count` — `48`                   | `availableQuantity`                                       |
 * | `Updated` — `2 min ago`          | `updatedAt`, as an age                                    |
 * | the three freshness badges       | derived, per G-36 and the design's own note               |
 * | `unknown · not reported`         | **not drawable** — a SKU with no row has never been counted |
 * | the four tiles                   | no listing publishes a total (R-04); no tile row here     |
 * | the primary command              | drawn and refused — `UPDATE_REFUSAL`                      |
 *
 * **This board needs a location as well as a merchant, which no other board on this platform
 * does.** COM-03's rows are keyed `(tenant, merchant, location, sku)`, and a merchant with two
 * branches has two shelves — so the design's own `Branch (select)` on its update form is the
 * reading that matters. The location comes from the session, and when it is absent this says so
 * rather than picking one: a shelf shown for the wrong branch is a count a merchant acts on.
 *
 * **The clock is re-read on a timer**, because the `Updated` column and all three badges are ages.
 * A board whose `2 min ago` says `2 min ago` an hour later is a board reporting a freshness that is
 * not true. It ticks every fifteen seconds — slower than the orders board's second, because a
 * minute's resolution does not need a second's — and fetches nothing.
 */

const inventory = services.commerce__inventory_availability;

export interface StockProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `EAM › Today › Stock`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Stock";
const SOURCE = "COM-03";
const DESCRIPTION =
  'Report stock so customers see a live count. Items without one show as "not reported".';
const COLUMNS = ["Product", "Count", "Updated"] as const;
const NOTE = "G-36: the service stores only a count and time, so freshness is worked out from age.";
/** The design's own `noteK` for this page is `warn`, which its `NK` map draws in the amber tint. */
const NOTE_KIND = "warn" as const;
/** The design's own `FORMS.stock[0]`, which is the word on its heading button. */
const PRIMARY = "Update stock";

/** The most rows one request answers, which is this listing's own maximum. */
const PER_FETCH = 200;

export function Stock({ transport, session, crumbs }: StockProps): ReactNode {
  const [rows, setRows] = useState<readonly Availability[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");
  /*
   * The clock, as state, so the ages move without a fetch.
   *
   * Fifteen seconds. The column reads in minutes, so a second's resolution would re-render fifteen
   * times for one visible change — and unlike the orders board there is no countdown here that a
   * merchant is watching tick.
   */
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 15_000);
    return (): void => {
      clearInterval(tick);
    };
  }, []);

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    if (!session.scopes.has(INVENTORY_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [INVENTORY_SCOPE] });
      return;
    }

    /*
     * The merchant and the branch, both from the token.
     *
     * This is the only board on the platform that needs two identifiers beyond the tenant, and
     * neither may come from the browser: a shelf read for the wrong branch is a count a merchant
     * acts on. Said as a forbidden state, because that is what a token short of a claim is.
     */
    const merchantId = session.merchantId;
    const locationId = session.locationId;
    if (merchantId === undefined || locationId === undefined) {
      setFailure({
        kind: "forbidden",
        missingScopes: [
          merchantId === undefined ? "a merchant_id claim on your token" : "",
          locationId === undefined ? "a location_id claim on your token" : "",
        ].filter((need) => need.length > 0),
      });
      return;
    }

    try {
      const page = await inventory
        .createInventoryAvailabilityClient(transport)
        .listInventoryAvailability(merchantId, locationId, String(PER_FETCH));
      setRows(page.availability);
      setTruncated(typeof page.nextAfter === "string");
    } catch (error) {
      setFailure(stateForError(error, [INVENTORY_SCOPE]));
    }
  }, [transport, session.scopes, session.merchantId, session.locationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardRows =
    rows === undefined
      ? undefined
      : rows
          .filter((row) => state === "" || freshnessOf(row, now).label === state)
          .map((row) => stockRow(row, now));

  const bounded = boundedNote(rows?.length, truncated, "products", "ordered by SKU");
  const footnote = listingNote(rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Nothing has been counted at this branch yet. A row appears here once stock is reported for a product."
      primary={{ label: PRIMARY, refusal: UPDATE_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows, now)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "warning", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(footnote === undefined ? {} : { footnote })}
      exportName="stock"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
